import { useState } from "react";
import toast from "react-hot-toast";
import { useSetApiKey } from "utils/api/api_keys.api";
import { type SetApiKeyRequest, type ApiKey } from "utils/types/api_keys.type";

interface FormData {
  id: string;
  key: string;
  value: string;
  label: string;
}

interface UseApiKeyManagementReturn {
  // Form state
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  editingId: string | null;
  
  // Modal state
  isModalOpen: boolean;
  
  // Loading states
  isSetting: boolean;
  
  // Handlers
  handleOpenModal: (apiKey?: ApiKey) => void;
  handleCloseModal: () => void;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: () => void;
}

export const useApiKeyManagement = (): UseApiKeyManagementReturn => {
  const { mutate: setApiKey, isLoading: isSetting } = useSetApiKey();
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({
    id: "",
    key: "",
    value: "",
    label: "",
  });

  const handleOpenModal = (apiKey?: ApiKey) => {
    if (apiKey) {
      setEditingId(apiKey.id);
      setFormData({
        id: apiKey.id,
        key: apiKey.key,
        value: apiKey.value,
        label: apiKey.label,
      });
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    setFormData({
      id: "",
      key: "",
      value: "",
      label: "",
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = () => {
    if (!formData.id || !formData.value) {
      toast.error("Value is required");
      return;
    }

    const requestData: SetApiKeyRequest = {
      id: formData.id,
      value: formData.value,
    };

    setApiKey(requestData, {
      onSuccess: (response) => {
        toast.success(response.message || "API key updated successfully!");
        handleCloseModal();
      },
      onError: (error) => {
        toast.error(error?.message || "Failed to update API key. Please try again.");
      },
    });
  };

  return {
    formData,
    setFormData,
    editingId,
    isModalOpen,
    isSetting,
    handleOpenModal,
    handleCloseModal,
    handleChange,
    handleSubmit,
  };
};

